import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Check } from 'lucide-react';
import { openCookieSettings } from './CookieConsent';

const scrollToHash = (hash) => {
  if (!hash) return;
  
  const id = hash.includes('#') ? hash.split('#')[1] : hash;
  const element = document.getElementById(id);
  
  if (element) {
    setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }
};

const Footer = () => {
  const [emailText, setEmailText] = React.useState('hi@plottie.de');
  const [showCheck, setShowCheck] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleEmailClick = (e) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      return; // Let the default mailto: behavior work on mobile
    }
    
    e.preventDefault();
    navigator.clipboard.writeText('hi@plottie.de');
    setEmailText('E-Mail Adresse kopiert.');
    setShowCheck(true);
    
    // Reset text and icon after 2 seconds
    setTimeout(() => {
      setEmailText('hi@plottie.de');
      setShowCheck(false);
    }, 2000);
  };

  const handleHashClick = (e) => {
    e.preventDefault();
    const hash = e.currentTarget.getAttribute('href');
    
    // If we're not on the landing page, navigate there first
    if (location.pathname !== '/') {
      navigate(hash); // This will preserve the hash
      return;
    }
    
    scrollToHash(hash);
  };

  return (
    <footer className="bg-[#FFF3DD] text-[#343434]">
      {/* Decorative Footer Image */}
      <div className="w-full relative select-none pointer-events-none" aria-hidden="true">
        <img
          src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/footer-deco-qeD3pts3cCvDUvK9Y5X2MMgg2w172y.png"
          alt=""
          className="w-full"
        />
      </div>
      
      <div className="py-16 px-8 lg:py-24">
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto flex flex-col lg:flex-row lg:justify-between items-start lg:space-x-8">
          {/* Logo Section */}
          <div className="mb-8 lg:mb-0 lg:w-1/4 flex flex-col items-start">
            <Link to="/" className="mb-6">
              <img 
                src="/Logo.svg" 
                alt="Plottie"
                className="h-12 w-auto"
              />
            </Link>
          </div>
          
          {/* Navigation Links */}
          <div className="flex flex-col space-y-4 mb-8 lg:mb-0 lg:w-1/4 items-start font-aleo">
            <Link to="/product" className="hover:text-[#FFA23E] transition-colors">Produkt</Link>
            <Link 
              to="/#so-funktionierts" 
              onClick={handleHashClick} 
              className="hover:text-[#FFA23E] transition-colors"
            >
              So funktioniert's
            </Link>
            <Link 
              to="/#beispiele" 
              onClick={handleHashClick} 
              className="hover:text-[#FFA23E] transition-colors"
            >
              Beispiele
            </Link>
            <Link 
              to="/#warum-plottie" 
              onClick={handleHashClick} 
              className="hover:text-[#FFA23E] transition-colors"
            >
              Warum Plottie
            </Link>
            <Link to="/faq" className="hover:text-[#FFA23E] transition-colors">FAQ</Link>
          </div>
          
          {/* Legal Links */}
          <div className="flex flex-col space-y-4 mb-8 lg:mb-0 lg:w-1/4 items-start font-aleo">
            <Link to="/impressum" className="hover:text-[#FFA23E] transition-colors">Impressum</Link>
            <Link to="/agb#datenschutz" className="hover:text-[#FFA23E] transition-colors">Datenschutz</Link>
            <Link to="/agb#agb" className="hover:text-[#FFA23E] transition-colors">AGB</Link>
            <button 
              onClick={openCookieSettings} 
              className="hover:text-[#FFA23E] transition-colors text-left"
            >
              Cookie-Einstellungen
            </button>
          </div>

          {/* Social Media & Contact */}
          <div className="flex flex-col space-y-4 mb-8 lg:mb-0 lg:w-1/4 items-start">
            <div className="flex space-x-4">
              <a 
                href="mailto:hi@plottie.de" 
                className="font-aleo hover:text-[#FFA23E] transition-colors flex items-center gap-2"
                onClick={handleEmailClick}
              >
                {showCheck ? (
                  <Check size={16} className="relative top-[-1px]" />
                ) : (
                  <Mail size={16} className="relative top-[-1px]" />
                )}
                {emailText}
              </a>
            </div>
            <div className="flex gap-8 lg:gap-4 pt-4">
              <a 
                href="https://instagram.com/plottiee" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:opacity-80 transition-opacity"
                aria-label="Instagram"
              >
                <img 
                  src="/ig.svg" 
                  alt="Instagram" 
                  className="w-8 h-8 lg:w-6 lg:h-6"
                />
              </a>
              <a 
                href="https://tiktok.com/plottiee" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:opacity-80 transition-opacity"
                aria-label="TikTok"
              >
                <img 
                  src="/tiktok.svg" 
                  alt="TikTok" 
                  className="w-8 h-8 lg:w-6 lg:h-6"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 